import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faUserHeadset } from "@fortawesome/pro-light-svg-icons";
import { faPhone, faPhoneFlip } from "@fortawesome/pro-regular-svg-icons";
import { faTruckMedical } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography, useMediaQuery } from "@mui/material";
import parse from 'html-react-parser';
import appStoreImg from '../../assets/images/AppStoreImg.png';
import playStoreImg from '../../assets/images/PlayStoreImg.png';
import { UnimedContact } from "../../interfaces/entities/FaleComUnimed/UnimedContact";
import { sxContactCardPhone, sxContactCardSubTitle, sxContactCardTitle, sxContactCardWebLink } from "./sxProps";
import { useAccessibility } from "../../Store/contexts/AccessibilityContext";
import { enhanceAccessibility } from "../../utils/EnhancedAccessibility";

const ContactCard: React.FC<{ contato: UnimedContact }> = ({ contato }) => {

  const accessibility = useAccessibility();

  const { title, telefones, subTitle1, subTitle2, lojaApp } = { ...contato };
  const mobileDevice = useMediaQuery('(max-width: 899px)');

  const englishWords = [
    'help', 'app', 'WhatsApp', 'Google', 'Play', 'Store', 'Android', 'iPhone' 
  ]

  const getContactIcon = () => {
    switch (title) {
      case 'Central de Relacionamento':
        return faPhoneFlip;
      case 'Ambulância Unimed Help':
        return faTruckMedical;
      case 'WhatsApp':
        return faWhatsapp;
      case 'Ouvidoria':
        return faPhoneFlip;
      case 'Camila':
        return faUserHeadset;
      case 'Vendas':
        return faPhoneFlip;
      case 'Deficientes Auditivos':
        return faPhoneFlip;
      case 'WhatsApp Vendas':
        return faWhatsapp;
      case 'App':
        return faPhone;
      default:
        return faPhone;
    }
  }

  const getContactTitle = () => {
    switch (title) {
      case 'Central de Relacionamento':
        return `<b>${title}</b>`;
      case 'Ambulância Unimed Help':
        return `<b>${title}</b>`;
      case 'WhatsApp':
        return `<b>${title}</b>`;
      case 'Ouvidoria':
        return `<b>${title}</b>`;
      case 'Camila':
        return '<b>Camila, nossa assistente virtual</b>';
      case 'Vendas':
        return '<b>Central de Vendas</b>';
      case 'Deficientes Auditivos':
        return `<b>${title}</b>`;
      case 'WhatsApp Vendas':
        return '<b>WhatsApp Vendas</b> ';
      case 'App':
        return '<b>Aplicativo Unimed Campinas</b>';
      default:
        return '';
    }
  }

  const getAppImg = (appName: string) => {
    return (appName === 'PLAYSTORE') ? playStoreImg : appStoreImg;
  }

  const contactNotClickable = (contact: string | undefined) => {
    return !mobileDevice && contact?.startsWith('0800')
  }

  const handleContactClick = (contact: string) => {
    if (contactNotClickable(contact)) {
      return;
    }
    if (contact.startsWith('0800')) { //DISCAR PARA O 0800 DA UNIMED EM DISPOSITIVOS MÓVEIS
      if (mobileDevice) {
        const telUrl = `tel:${contact}`;
        window.open(telUrl, '_blank');
      }
    } else if (contact.includes('www')) { //LINK PARA ABRIR SITE
      const url = `https://${contact}`;
      window.open(url, '_blank');
    } else { //LINK PARA WHATSAPP
      const whatsappUrl = `https://wa.me/55${contact.replace(/\D/g, '')}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  return (
    <div className='contact-card' tabIndex={0} style={{...accessibility.contrastStyle, ...accessibility.contrastBorderAccessibility}}>
      <Typography component="h2" style={accessibility.textStyleAccessibility} sx={sxContactCardTitle}>{parse(enhanceAccessibility(getContactTitle(), englishWords))}</Typography>
      {!!telefones?.length &&
        <div 
          className={`icon-phone-holder ${contactNotClickable(telefones.at(0)) && 'not-clickable'}`}
          tabIndex={contactNotClickable(telefones.at(0)) ? -1 : 0}
          onClick={() => handleContactClick(telefones.at(0) || '')}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleContactClick(telefones.at(0) || "");
            }
          }}
        >
          <FontAwesomeIcon style={accessibility.contrastColorAccessibility} className="fa-icon-contact-card" icon={getContactIcon() as IconProp} />
          <Typography
            style={accessibility.textTitleStyleAccessibility}
            sx={title === 'Camila' ? sxContactCardWebLink : sxContactCardPhone}
          >
            {parse(telefones.at(0) || '')}
          </Typography>
        </div>
      }
      {!!subTitle1 && <Typography style={accessibility.calculateTextStyle(12)} sx={sxContactCardSubTitle}>{parse(enhanceAccessibility(subTitle1, englishWords))}</Typography>}
      {!!subTitle2 && <Typography style={accessibility.calculateTextStyle(12)} sx={sxContactCardSubTitle}>{parse(enhanceAccessibility(subTitle2, englishWords))}</Typography>}
      {lojaApp &&
        lojaApp.map((linkLoja) => (
          <div className="app-install-holder" key={linkLoja.title}>
            <Typography style={accessibility.calculateTextStyle(12)} sx={sxContactCardSubTitle} role="text">
              {parse(enhanceAccessibility(linkLoja.title === 'PLAYSTORE' ? 'Versão para Android, disponível no Google Play:' : 'Versão para iPhone, disponível na App Store:', englishWords))}
            </Typography>
            <img
              className="image-button"
              src={getAppImg(linkLoja.title)} alt={`Link para instalar o aplicativo na loja ${linkLoja.title}`}
              onClick={() => window.open(linkLoja.link, '_blank')}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  window.open(linkLoja.link, "_blank");
                }
              }}
            />
          </div>
        ))
      }
    </div>
  )
}

export default ContactCard;